import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    AvoirAcompteModelDto,
    CompteEcritureDto,
    CriteresMouvementComptableDtoInput,
    LettrageModel,
    MutationDelettrerEcrituresArgs,
    MutationGetIdExerciceOfEcritureArgs,
    MutationGetIdExerciceOfFactureArgs,
    MutationGetListeLettrageModelArgs,
    MutationGetListeLettreEcrituresFromFactureArgs
} from 'src/graphql/generated-types';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { KAL_DIALOG_DATA, KalDialogRef } from '@kalidea/kaligraphi';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import gql from 'graphql-tag';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserState } from 'src/app/_user/store/user/user.state';

export interface ActivityAccountingLetteringDialogData {
    accepted?: boolean;
    accountingEntry?: CompteEcritureDto;
    credit?: AvoirAcompteModelDto;
}

@Component({
    selector: 'app-shared-accounting-lettering-dialog',
    templateUrl: './shared-accounting-lettering-dialog.component.html',
    styleUrls: ['./shared-accounting-lettering-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAccountingLetteringDialogComponent extends BaseComponent implements OnInit {

    dataSource: LettrageModel[] = [];

    columns: KalTableHeader<LettrageModel>[] = [
        {code: 'Lettre', label: 'Lettre', sortable: true},
        {code: 'Utilisateur', label: 'Utilisateur', sortable: true},
        {code: 'NomDelegCibleEcriture', label: 'Délégation cible', sortable: true},
        {code: 'CodeJournal', label: 'Journal', sortable: true},
        {code: 'NumeroPiece', label: 'Num. pièce', sortable: true},
        {code: 'DateEcriture', label: 'Date', sortable: true},
        {code: 'CompteCollectif', label: 'Collectif', sortable: true},
        {code: 'NomPrenomBenef', label: 'Tiers', sortable: true},
        {code: 'LibelleMouvement', label: 'Libellé', sortable: true},
        {code: 'Debit', label: 'Débit', sortable: true},
        {code: 'Credit', label: 'Crédit', sortable: true},
    ];

    constructor(private readonly dialogRef: KalDialogRef<SharedAccountingLetteringDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public data: ActivityAccountingLetteringDialogData,
                protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService) {
        super(cdr);
    }

    get accountingEntry(): CompteEcritureDto {
        return this.data.accountingEntry;
    }

    get credit(): AvoirAcompteModelDto {
        return this.data.credit;
    }

    close(): void {
        this.dialogRef.close();
    }

    submit(): void {
        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'DelettrerEcritures', MutationDelettrerEcrituresArgs>({
            mutation: gql`
                mutation DelettrerEcritures($contexte: ContextInfosInput, $ListeIdEcritures: ArrayOfdecimalInput) {
                    DelettrerEcritures(
                        contexte: $contexte
                        ListeIdEcritures: $ListeIdEcritures
                    )
                }
            `,
            variables: {
                ListeIdEcritures: {
                    decimal: this.dataSource.map(e => e.IdEcriture)
                }
            },
        }).pipe(
            tap(() => {
                this.loading = false;
                this.data.accepted = true;
                this.dialogRef.close(this.data);
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    private getExerciceIdOfInvoice(): Observable<number> {
        return this.graphqlService.mutateOne<'GetIdExerciceOfFacture', MutationGetIdExerciceOfFactureArgs>({
            mutation: gql`
                mutation GetIdExerciceOfFacture($contexte: ContextInfosInput, $IdFacture: Float) {
                    GetIdExerciceOfFacture(
                        contexte: $contexte
                        IdFacture: $IdFacture
                    )
                }
            `,
            variables: {
                IdFacture: this.credit.IdFacture
            },
        });
    }

    private getExerciceIdOfAccounting(): Observable<number> {
        return this.graphqlService.mutateOne<'GetIdExerciceOfEcriture', MutationGetIdExerciceOfEcritureArgs>({
            mutation: gql`
                mutation GetIdExerciceOfEcriture($contexte: ContextInfosInput, $IdEcriture: Float) {
                    GetIdExerciceOfEcriture(
                        contexte: $contexte
                        IdEcriture: $IdEcriture
                    )
                }
            `,
            variables: {
                IdEcriture: this.accountingEntry?.Id
            },
        });
    }

    private getLetterFromPayment(): Observable<string> {
        return of(this.accountingEntry.ListeMouvementComptable.MouvementComptableDTO
            .filter(({Lettre}) => !!Lettre)
            .map(({Lettre}) => Lettre)[0]);
    }

    private GetInvoicesLettersListFromInvoice(): Observable<string[]> {
        return this.graphqlService.mutateOne<'GetListeLettreEcrituresFromFacture', MutationGetListeLettreEcrituresFromFactureArgs>({
            mutation: gql`
                mutation GetListeLettreEcrituresFromFacture(
                    $contexte: ContextInfosInput,
                    $IdFacture: Float
                ) {
                    GetListeLettreEcrituresFromFacture(
                        contexte: $contexte
                        IdFacture: $IdFacture
                    ) {
                        string
                    }
                }
            `,
            variables: {
                IdFacture: this.credit.IdFacture
            },
        }).pipe(
            map(GetListeLettreEcrituresFromFacture => GetListeLettreEcrituresFromFacture.string)
        );
    }

    private getAccountingLetteringsList(): void {
        this.loading = true;
        let result: Observable<number> = null;
        const config: CriteresMouvementComptableDtoInput = {};

        if (this.accountingEntry) {
            result = this.getExerciceIdOfAccounting().pipe(
                switchMap(exercice => this.getLetterFromPayment().pipe(
                    map(e => {
                        config.LettreComptable = e;
                        return exercice;
                    })
                ))
            );
        } else {
            result = this.getExerciceIdOfInvoice().pipe(
                switchMap(exercice => this.GetInvoicesLettersListFromInvoice().pipe(
                    map(e => {
                        config.ListeLettreComptable = {string: e};
                        return exercice;
                    })
                ))
            );
        }

        this.watch$ = result.pipe(
            switchMap(exerciceId => {
                return this.graphqlService.mutateOne<'GetListeLettrageModel', MutationGetListeLettrageModelArgs>({
                    mutation: gql`
                        mutation GetListeLettrageModel(
                            $contexte: ContextInfosInput,
                            $Criteres: CriteresMouvementComptableDTOInput
                        ) {
                            GetListeLettrageModel(
                                contexte: $contexte
                                Criteres: $Criteres
                            ) {
                                NombreElementTotal
                                ListeLettrageModel {
                                    LettrageModel {
                                        IdEcriture
                                        Lettre
                                        Utilisateur
                                        NomDelegCibleEcriture
                                        CodeJournal
                                        NumeroPiece
                                        DateEcriture
                                        CompteCollectif
                                        IdBenef
                                        NomPrenomBenef
                                        IdFournisseur
                                        NomFournisseur
                                        LibelleMouvement
                                        Debit
                                        Credit
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        Criteres: {
                            IsSupprime: false,
                            IsEcritureSimulee: false,
                            IdJournalForTypeBudget: this.accountingEntry?.Journal?.Id,
                            IdExercice: exerciceId,
                            IdCECibleSource: this.snapshot(UserState.userContext)?.CurrentCE || null,
                            ...config
                        },
                    }
                });
            }),
            tap(GetListeLettrageModel => {
                this.loading = false;
                this.dataSource = GetListeLettrageModel?.ListeLettrageModel?.LettrageModel || [];
                this.cdr.markForCheck();
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    ngOnInit(): void {
        this.getAccountingLetteringsList();
    }
}
